import { Flex } from 'foundations-components/components/Flex';
import { Dropdown } from 'foundations-components/transitional/components/Dropdown';
import { View } from 'foundations-components/transitional/components/View';
import { List } from 'foundations-components/transitional/components/List';
import { Button } from 'foundations-components/transitional/components/Button';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { EllipsesIcon } from 'foundations-assets/icons/components/EllipsesIcon';
import { ExternalLinkIcon } from 'foundations-assets/icons/components/ExternalLinkIcon';
import { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { fetchRevisions, revertThemeSettings } from '../../actions/ThemeSettingsActions';
import { getHasBufferedChanges } from '../../selectors/themeSettingsMetaSelectors';
import { getRevertThemeSettingsRequestStatus, getRevisions, getRevisionsRequestStatus } from '../../selectors/revisionsSelectors';
import { isSucceeded, isUnfulfilled } from '../../utils/requestStatus';
import usePrevious from 'react-utils/hooks/usePrevious';
import { UNINITIALIZED } from '../../constants/RequestStatusTypes';
import { IconHolder } from 'foundations-components/transitional/components/IconHolder';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const i18nKey = key => `theme-previewer.themeSettings.${key}`;
const UniversalThemeHeaderMenu = ({
  handleOpenRevisionsModal,
  handleClearThemeSettings,
  handleOpenFullThemeEditor,
  canAccessThemeEdit
}) => {
  var _revisions$;
  const dispatch = useDispatch();
  const revisions = useSelector(getRevisions);
  const hasBufferedChanges = useSelector(getHasBufferedChanges);
  const revertThemeSettingsRequestStatus = useSelector(getRevertThemeSettingsRequestStatus);
  const revisionsRequestStatus = useSelector(getRevisionsRequestStatus);
  const dropdownButton = /*#__PURE__*/_jsx(Flex, {
    alignSelf: "left",
    children: /*#__PURE__*/_jsx(EllipsesIcon, {
      size: "300",
      color: "icon-interactive-default"
    })
  });
  const previousRevertThemeSettingsRequestStatus = usePrevious(revertThemeSettingsRequestStatus);
  useEffect(() => {
    if (!revisions.length && revisionsRequestStatus === UNINITIALIZED) {
      dispatch(fetchRevisions());
    }
    if (isSucceeded(revertThemeSettingsRequestStatus) && isUnfulfilled(previousRevertThemeSettingsRequestStatus)) {
      window.location.reload();
    }
  }, [revisions, dispatch, revertThemeSettingsRequestStatus, previousRevertThemeSettingsRequestStatus, revisionsRequestStatus]);
  const lastPublishedRevision = (_revisions$ = revisions[0]) === null || _revisions$ === void 0 ? void 0 : _revisions$.id;
  const handleResetToLastPublish = () => {
    dispatch(revertThemeSettings(lastPublishedRevision));
  };
  return /*#__PURE__*/_jsxs(Dropdown, {
    "data-test-id": "reset-to-default-dropdown",
    autoPlacement: "vert",
    buttonUse: "transparent",
    buttonText: dropdownButton,
    buttonStyles: {
      paddingInline: '0',
      paddingBlock: '0'
    },
    caretRenderer: null,
    placement: "bottom left",
    children: [/*#__PURE__*/_jsx(View, {
      borderBlockEndStyle: "solid",
      borderBlockEndColor: "border-core-default",
      borderBlockEndWidth: "100",
      paddingBlockEnd: "200",
      children: /*#__PURE__*/_jsxs(List, {
        wrapChildren: true,
        children: [lastPublishedRevision && /*#__PURE__*/_jsx(Button, {
          "data-test-id": "reset-to-default",
          onClick: handleResetToLastPublish,
          use: "unstyled",
          paddingInlineEnd: "200",
          external: true,
          disabled: !canAccessThemeEdit || !hasBufferedChanges,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: i18nKey('universal.headerMenu.resetToLastPublish')
          })
        }), /*#__PURE__*/_jsx(Button, {
          "data-test-id": "reset-to-default",
          onClick: handleClearThemeSettings,
          use: "unstyled",
          paddingInlineEnd: "200",
          external: true,
          disabled: !canAccessThemeEdit,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: i18nKey('universal.headerMenu.resetButton')
          })
        })]
      })
    }), /*#__PURE__*/_jsx(View, {
      paddingBlockStart: "200",
      borderBlockEndStyle: "solid",
      borderBlockEndColor: "border-core-default",
      borderBlockEndWidth: "100",
      children: /*#__PURE__*/_jsxs(List, {
        wrapChildren: true,
        children: [/*#__PURE__*/_jsxs(Button, {
          "data-test-id": "reset-to-default",
          onClick: handleOpenFullThemeEditor,
          use: "link",
          paddingInlineEnd: "200",
          children: [/*#__PURE__*/_jsx(IconHolder, {
            icon: ExternalLinkIcon,
            size: "200",
            paddingInlineStart: "0",
            paddingInlineEnd: "100"
          }), /*#__PURE__*/_jsx(FormattedMessage, {
            message: i18nKey('universal.headerMenu.themeEditorAppLink')
          })]
        }), /*#__PURE__*/_jsxs(Button, {
          "data-test-id": "reset-to-default",
          onClick: handleOpenRevisionsModal,
          use: "link",
          paddingInlineEnd: "200",
          paddingBlockEnd: "200",
          children: [/*#__PURE__*/_jsx(IconHolder, {
            icon: ExternalLinkIcon,
            size: "200",
            paddingInlineStart: "0",
            paddingInlineEnd: "100"
          }), /*#__PURE__*/_jsx(FormattedMessage, {
            message: i18nKey('universal.headerMenu.versionHistoryLink')
          })]
        })]
      })
    }), /*#__PURE__*/_jsx(View, {
      paddingBlockStart: "200",
      children: /*#__PURE__*/_jsx(List, {
        wrapChildren: true,
        children: /*#__PURE__*/_jsxs(Button, {
          "data-test-id": "reset-to-default",
          onClick: () => window.open('https://knowledge.hubspot.com/website-pages/use-themes'),
          use: "link",
          paddingInlineEnd: "200",
          children: [/*#__PURE__*/_jsx(IconHolder, {
            icon: ExternalLinkIcon,
            size: "200",
            paddingInlineStart: "0",
            paddingInlineEnd: "100"
          }), /*#__PURE__*/_jsx(FormattedMessage, {
            message: i18nKey('universal.headerMenu.learnMore')
          })]
        })
      })
    })]
  });
};
export default UniversalThemeHeaderMenu;